// SPDX-License-Identifier: AGPL-3.0-or-later

import type {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import GuildMatureContentAgree from '@app/features/guild/state/GuildMatureContentAgree';
import ReadStates from '@app/features/read_state/state/ReadStates';
import UserGuildSettings from '@app/features/user/state/UserGuildSettings';
import {extractTimestamp} from '@konvo/snowflake/src/SnowflakeUtils';

/** Latest activity used to order unread server-channel conversations. */
export function unreadChannelActivityAt(channel: Channel): number {
	const lastMessageId = ReadStates.lastMessageId(channel.id) ?? channel.lastMessageId ?? null;
	return lastMessageId ? extractTimestamp(lastMessageId) : channel.createdAt.getTime();
}

/**
 * Channels which belong in the existing Notifications > Unreads inbox.
 * Keeping this rule shared prevents Chats and the desktop Rail from surfacing
 * muted, hidden, or mature-content-gated activity that the inbox would omit.
 */
export function getUnreadChannels(): Array<Channel> {
	const channels: Array<Channel> = [];
	for (const channelId of ReadStates.getChannelIds()) {
		const channel = Channels.getChannel(channelId);
		if (!channel) continue;
		const hasUnread = ReadStates.hasUnread(channel.id);
		const hasMentions = ReadStates.getMentionCount(channel.id) > 0;
		if (!hasUnread && !hasMentions) continue;
		if (
			!UserGuildSettings.shouldShowChannelInUnreadInbox(
				{
					id: channel.id,
					guildId: channel.guildId ?? undefined,
					parentId: channel.parentId ?? undefined,
					type: channel.type,
				},
				{hasUnread, hasMentions},
			)
		) {
			continue;
		}
		if (GuildMatureContentAgree.shouldShowGate({channelId: channel.id, guildId: channel.guildId ?? null})) continue;
		channels.push(channel);
	}
	return channels.sort((a, b) => unreadChannelActivityAt(b) - unreadChannelActivityAt(a));
}

/** Server activity only; direct messages already have their own Chats rows. */
export function getUnreadServerChannels(): Array<Channel> {
	return getUnreadChannels().filter((channel) => channel.guildId != null);
}
